import React, { useMemo } from "react";
import createIOSortType from "../../../../../../lib/createIOSortType";
import createGlobalState from "@ui/lib/createGlobalState";
import Table from "@ui/Table";
import type { ColumnMetaData } from "@ui/Table/ColumnMetaData";
import { ROW_SELECTOR_SIZE_PX, SELECTABLE_ROW_CHECKBOX_CLASSNAME } from "@ui/Table/useSelectedRows";
import Ellipsis from "@ui/Ellipsis";
import { css } from "@linaria/core";
import { color, fonts } from "@ui";
import { colors, spacings } from "@ui/Theme";
import type { Project } from "../../../../../../api/useProjects";
import { useTranslations } from "../../../../../../api/useTranslations";
import type { Translation } from "../../../../../../api/useTranslations";
import useSortedData from "../../../../../../lib/hooks/useSortedData";
import useDistinctShape from "@ui/lib/hooks/useDistinctShape";
import cx from "classnames";
import type { SortableValue } from "diagnostics-lib/comparator";
import t from "../../../../../../i18n";
import formatDate from "@ui/i18n/formatDate";
import {
  Chat4Line,
  FlagLine,
  GitCommitLine,
  InboxArchiveLine,
  InformationLine,
  LinkUnlinkM,
} from "@ui/IconV2/react";
import formatNumber from "@ui/i18n/formatNumber";
import { useLocation, useNavigate } from "react-router-dom";
import Text from "@ui/Text";
import TooltipsV2 from "@ui/TooltipV2";
import OverlayWrapper from "@ui/TooltipV2/OverlayWrapper";
import { millisecondsToFractional } from "../../../utils/millisecondsToFractional";
import { getEventStatusWeight } from "../../../utils/faultEventStatus";
import { getDetectionTag } from "../../../utils/faultEvent";
import Marker from "@ui/Marker";
import ButtonIconV2 from "@ui/ButtonIconV2";
import { DEFAULT_CONFIG, Markdown } from "@ui/Markdown";
import { useComments } from "../../../../../../api/useComments";
import { analysisTitleShort } from "../../../../Analysis/analysisTitle";
import { groupBy } from "diagnostics-lib/iterable";
import type { Index } from "diagnostics-lib/iterable";
import type { Options } from "@ui/Highcharts";
import TooltipV2 from "@ui/TooltipV2";
import {
  aggregationPeriodLabel,
  aggregationPeriodLabelLong,
  type AggregationPeriod,
} from "../../../../../../domain/aggregation/period";
import { listEventSourceIds } from "diagnostics-domain/faultEvent";

import type { Quantity } from "../../../../../../api/useQuantities";
import type { Event, EventDiagnosed } from "../../../../../../api/useEvents";
import { Tag } from "@ui/Tag";
import {
  getComputeRowKey,
  getEventDurationEndsAt,
  getEventEndsAt,
  getEventId,
  getEventIdentifiedAction,
  getEventLastActivityAt,
  getEventPriority,
  getEventSystems,
  isMultiFaultEvent,
} from "../../../../../../domain/event";
import {
  isEventSelectableForGrouping,
  canEventBeUngrouped,
} from "../../../../../../domain/event/grouping";
import { getEventpriorityOrder, getEventPriorityText, gotoEventPage } from "../../../utils/event";
import { toBasenamePath } from "../../../../../../router/helpers/navigation";
import PriorityFill from "@ui/IconV2/react/PriorityFill";
import { BadgeEventStatus } from "../BadgeEventStatus";
import { badgeTextOverflow } from "@ui/Badge/_styles/BadgeTextOverflow";
import EventTableSparkline from "../../../components/EventTableSparkline";
import { getLatestActiveAnalysisDate } from "../../../../latestActiveAnalysisDate";
import { EventName } from "../../../components/EventName";

export const columns = [
  "priority",
  "name",
  "status",
  "system",
  "startsAt",
  "endsAt",
  "averageLosses",
  "reparability",
  "action",
  "lastActivityAt",
  "lastComment",
  "type",
  "active",
] as const;

export type Column = (typeof columns)[number];

export const getLiveEventTableColumnsLabel = (
  column: Column,
  translations: Translation,
  duration?: string,
) => {
  switch (column) {
    case "priority":
      return t("project.plant_state.block.priority_event.table.priority");
    case "name":
      return t("project.fault_events.table.live.metadata.name");
    case "system":
      return t("project.fault_events.table.live.metadata.system");
    case "status":
      return t("project.fault_events.table.live.metadata.status");
    case "startsAt":
      return t("project.fault_events.table.live.metadata.start_date");
    case "endsAt":
      return t("project.fault_events.table.live.metadata.end_date");
    case "lastActivityAt":
      return t("project.fault_events.table.live.metadata.last_activity_date");
    case "reparability":
      return t("project.fault_events.table.live.metadata.reparability");
    case "action":
      return t("project.fault_events.table.live.metadata.work_request");
    case "lastComment":
      return t("project.fault_events.table.live.metadata.comments");
    case "averageLosses":
      return t(
        "project.fault_events.table.live.metadata.average_losses",
        translations,
        duration ?? "",
      );
    default:
      return "";
  }
};
export const AVERAGE_LOSSES_COLUMN = "averageLosses";

const useSortEvent = createGlobalState({
  storage: "session",
  key: "useSortEvent",
  type: createIOSortType(columns.filter(c => c !== "type" && c !== "active")),
  defaultValue: { column: "name", direction: "asc" },
  scoped: true,
});

type EventInput = Omit<Event, "id" | "failureValues">;
interface P {
  project: Project;
  performanceIndicator: Quantity | null;
  events: Event[];
  sparklineStatisticByFaultEventId: Index<
    {
      id: string;
      mean: number;
    },
    string
  >;
  loading: boolean;
  isHiddenColumn: (column: Column | string) => boolean;
  sparklineOptionsByFaultEventId: Index<
    {
      id: string;
      options: Options;
    },
    string
  >;
  aggregationPeriod: AggregationPeriod;
  groupingSelectionEnabled?: boolean;
  allEvents: Event[];
  selectedEvents?: EventDiagnosed[];
  onRowsSelection?: (events: Event[]) => void;
  onUngroupEvent?: (event: EventDiagnosed) => void;
}

const HAS_COLUMN_AVERAGE_LOSS = "HAS_COLUMN_AVERAGE_LOSS";
const computeRowKey = (event: Event) => getComputeRowKey(event);
const isAroundZero = (mean: number) => {
  const absoluteValue = Math.abs(mean);
  return absoluteValue > 0 && absoluteValue < 0.01;
};

const LiveEventTable = ({
  project,
  performanceIndicator,
  events,
  sparklineStatisticByFaultEventId,
  loading,
  isHiddenColumn,
  sparklineOptionsByFaultEventId,
  aggregationPeriod,
  groupingSelectionEnabled = false,
  allEvents,
  selectedEvents,
  onRowsSelection,
  onUngroupEvent,
}: P) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const translations = useTranslations();

  const comments = useComments(project.id);
  const commentsByFaultEvents = useMemo(
    () =>
      groupBy(
        comments
          .filter(comment => comment.faultEventId !== null)
          .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()),
        comment => comment.faultEventId,
      ),
    [comments],
  );
  const commentsByManualFaultEvents = useMemo(
    () => groupBy(comments, "manualFaultEventId"),
    [comments],
  );
  const allDiagnosedEvents = React.useMemo(
    () => allEvents.filter((event): event is EventDiagnosed => event.type === "DIAGNOSED"),
    [allEvents],
  );

  const [sort, setSort] = useSortEvent({ scope: project.id });
  const latestAnalysisDate = React.useMemo(() => getLatestActiveAnalysisDate(project), [project]);

  const sortSelector: Record<
    Exclude<Column, "type" | "active">,
    (e: Event) => SortableValue | null
  > = {
    priority: event => getEventpriorityOrder(getEventPriority(event)),
    name: event => event.name,
    status: event => getEventStatusWeight(event.metadata.status),
    startsAt: event => new Date().getTime() - new Date(event.startsAt).getTime(),
    endsAt: event => getEventDurationEndsAt(event),
    system: event => {
      const systemIds = getEventSystems(event);
      return systemIds.length === 0
        ? null
        : systemIds.sort((a, b) => a.localeCompare(b)).join(", ");
    },
    lastActivityAt: event => getEventLastActivityAt(event),
    reparability: event => event.reparability,
    action: event => getEventIdentifiedAction(event),
    averageLosses: event => {
      if (performanceIndicator === null) return null;
      const eventId = getEventId(event);

      const sparklineStatistic = sparklineStatisticByFaultEventId.get(eventId);
      const sparklineCharts = sparklineOptionsByFaultEventId.get(eventId);
      const mean = sparklineStatistic?.mean;

      if (!sparklineStatistic || !sparklineCharts || mean === undefined) return null;
      return mean;
    },
    lastComment: () => null,
  };
  const sortedDatas = useSortedData(events, sortSelector[sort.column], sort.direction);
  const stableSortedDatas = useDistinctShape(sortedDatas);

  const metadata: Record<Exclude<Column, "type" | "active">, ColumnMetaData<Event>> = React.useMemo(
    () => ({
      priority: {
        label: getLiveEventTableColumnsLabel("priority", translations),
        hidden: isHiddenColumn("priority"),
        render: event => (
          <Text className={eventPriority}>
            <PriorityFill priority={getEventPriority(event)} />
            {getEventPriorityText(getEventPriority(event))}
          </Text>
        ),
        minWidth: "140px",
        sortable: true,
        sticky: false,
        stickyBorder: false,
      },
      name: {
        label: getLiveEventTableColumnsLabel("name", translations),
        hidden: isHiddenColumn("name"),
        render: (event: Event) => (
          <div className={nameWrapper}>
            <EventName name={event.name} hasMultiFault={isMultiFaultEvent(event)} />{" "}
            {event.type === "MANUAL" && <Tag size="s">{t("project.event.type.manual")}</Tag>}
            {onUngroupEvent && canEventBeUngrouped(event) && (
              <TooltipV2 overlay={t("project.fault_events.table.live.action.ungroup")}>
                <ButtonIconV2
                  aria-label={t("project.fault_events.table.live.action.ungroup")}
                  className={ungroupButton}
                  onClick={clickEvent => {
                    clickEvent.stopPropagation();
                    onUngroupEvent(event);
                  }}
                >
                  <LinkUnlinkM size="s" />
                </ButtonIconV2>
              </TooltipV2>
            )}
          </div>
        ),
        minWidth: "300px",
        sortable: true,
        sticky: false,
        stickyBorder: false,
      },
      lastComment: {
        label: "",
        render: (event: Event) => {
          const commentsForEvent =
            event.type === "DIAGNOSED"
              ? [event.id, ...listEventSourceIds(event, allDiagnosedEvents)]
                  .flatMap(eventId => commentsByFaultEvents.get(eventId) ?? [])
                  .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
              : event.type === "MANUAL"
                ? (commentsByManualFaultEvents.get(event.id) ?? [])
                : [];

          const lastComment = commentsForEvent[0];
          if (commentsForEvent.length === 0 || !lastComment) {
            return (
              <Text className={cx(iconComment, "empty")} type="body1">
                <Chat4Line size="m" color={color.carbon900} />
                {commentsForEvent.length}
              </Text>
            );
          }

          return (
            <TooltipsV2
              placement="bottom"
              overlay={
                <OverlayWrapper
                  className={overlayWrapperLastComment}
                  text={
                    <div>
                      <Text type="h4" className={tooltipTitleLastComment}>
                        {t("project.fault_events.table.live.metadata.last_comment.tooltip.title")}
                      </Text>
                      {t("project.fault_events.table.live.metadata.last_comment.tooltip.content", {
                        analysis:
                          lastComment.analysisId && lastComment.metadata
                            ? analysisTitleShort(
                                {
                                  date: lastComment.metadata.date,
                                  group: lastComment.metadata.group,
                                  label: lastComment.metadata.label,
                                },
                                project.timezone,
                              )
                            : null,
                        author: lastComment.author.name ?? lastComment.author.id,
                        publishedAt: formatDate(lastComment.publishedAt, "relative", null),
                        content: (
                          <Markdown
                            config={{
                              ...DEFAULT_CONFIG,
                              theme: {
                                ...DEFAULT_CONFIG.theme,
                                paragraph: "paragraph-body2",
                              },
                            }}
                            className={actionTooltip}
                            defaultValue={lastComment.content}
                          />
                        ),
                      })}
                    </div>
                  }
                />
              }
            >
              <div>
                <Text className={iconComment} type="body1">
                  <Chat4Line size="m" color={color.carbon900} />
                  {commentsForEvent.length}
                </Text>
              </div>
            </TooltipsV2>
          );
        },
        minWidth: "50px",
        maxWidth: "50px",
        alignment: "right",
        sortable: false,
      },
      status: {
        label: getLiveEventTableColumnsLabel("status", translations),
        hidden: isHiddenColumn("status"),
        render: (b: EventInput) => (
          <BadgeEventStatus className={badgeTextOverflow} status={b.metadata.status} />
        ),
        minWidth: "200px",
        maxWidth: "200px",
        sortable: true,
      },
      system: {
        label: (
          <span className={lineClamp}>{getLiveEventTableColumnsLabel("system", translations)}</span>
        ),
        hidden: isHiddenColumn("system"),
        render: (event: Event) => {
          const systems = getEventSystems(event)
            .sort((a, b) => a.localeCompare(b))
            .join(", ");
          return systems ? <Ellipsis title={systems}>{systems}</Ellipsis> : null;
        },
        minWidth: "150px",
        maxWidth: "150px",
        sortable: true,
      },
      startsAt: {
        label: (
          <span className={lineClamp}>
            {getLiveEventTableColumnsLabel("startsAt", translations)}
          </span>
        ),
        hidden: isHiddenColumn("startsAt"),
        render: (b: Event) => {
          const duration = new Date().getTime() - new Date(b.startsAt).getTime();
          const fractional = millisecondsToFractional(duration);
          const date = formatDate(b.startsAt, "date", project.timezone);
          return (
            <div className={datetime}>
              <span>{date}</span>
              <span>{t("datetime.fractional", { type: "date", ...fractional })}</span>
            </div>
          );
        },
        minWidth: "200px",
        maxWidth: "200px",
        sortable: true,
      },
      endsAt: {
        label: (
          <span className={lineClamp}>{getLiveEventTableColumnsLabel("endsAt", translations)}</span>
        ),
        hidden: isHiddenColumn("endsAt"),
        render: (r: Event) => {
          const endsAt = getEventEndsAt(r);
          if (!endsAt) return null;

          const isDateNotExpired = (endsAt: Date) => endsAt <= new Date();
          const renderEndDateBlock = (endsAt: Date) => {
            const duration = new Date().getTime() - endsAt.getTime();
            const fractional = millisecondsToFractional(duration);
            const date = formatDate(endsAt, "date", project.timezone);

            return (
              <div className={containerEndDate}>
                <div className={datetime}>
                  <span>{date}</span>
                  {isDateNotExpired(endsAt) && (
                    <span>{t("datetime.fractional", { type: "date", ...fractional })}</span>
                  )}
                </div>
              </div>
            );
          };
          const renderFlags = (detectionTag: string | null, hasAnalysisToAnalyze: boolean) => {
            if (!detectionTag && !hasAnalysisToAnalyze) return null;

            return (
              <div className={flags}>
                {hasAnalysisToAnalyze && (
                  <TooltipsV2
                    placement="top"
                    overlay={
                      <OverlayWrapper
                        className={styledOverlayWrapper}
                        text={t("project.fault_event.tooltips.analysis_to_analyze")}
                      />
                    }
                  >
                    <div>
                      <Marker>
                        <div className={cx(flag)}>
                          <FlagLine size="s" color={colors.titanium800} />
                        </div>
                      </Marker>
                    </div>
                  </TooltipsV2>
                )}

                {detectionTag === "singleEvent" && (
                  <TooltipsV2
                    placement="top"
                    overlay={
                      <OverlayWrapper
                        className={styledOverlayWrapper}
                        text={t("project.fault_event.tooltips.single")}
                      />
                    }
                  >
                    <div className={flag}>
                      <GitCommitLine size="s" color={colors.titanium800} />
                    </div>
                  </TooltipsV2>
                )}

                {detectionTag === "closable" && (
                  <TooltipsV2
                    placement="top"
                    overlay={
                      <OverlayWrapper
                        className={styledOverlayWrapper}
                        text={t("project.fault_events.table.live.tooltips.closable")}
                      />
                    }
                  >
                    <div className={flag}>
                      <InboxArchiveLine size="s" color={colors.titanium800} />
                    </div>
                  </TooltipsV2>
                )}
              </div>
            );
          };

          switch (r.type) {
            case "DIAGNOSED": {
              const statusByAnalysis = r.metadata.faults.flatMap(fault => fault.statusByAnalysis);
              const detectionTag = getDetectionTag({
                isClosable: r.metadata.isClosable,
                statusByAnalysis,
              });
              const hasAnalysisToAnalyze = statusByAnalysis.some(
                status => status.status === "TO_BE_ANALYSED",
              );

              return (
                <div className={containerEndDate}>
                  {renderEndDateBlock(endsAt)}
                  {renderFlags(detectionTag, hasAnalysisToAnalyze)}
                </div>
              );
              break;
            }
            case "MANUAL": {
              return <div className={containerEndDate}>{renderEndDateBlock(endsAt)}</div>;
            }
            default:
              return null;
          }
        },
        minWidth: "253px",
        maxWidth: "253px",
        sortable: true,
      },
      averageLosses: {
        label: performanceIndicator ? (
          <div className={labelInfo}>
            <div className={lineClamp}>
              {getLiveEventTableColumnsLabel(
                "averageLosses",
                translations,
                (aggregationPeriod === "ALL"
                  ? aggregationPeriodLabelLong(aggregationPeriod)
                  : aggregationPeriodLabel(aggregationPeriod)
                ).toLocaleLowerCase(),
              )}
            </div>
            <TooltipV2
              placement="top"
              overlay={
                <OverlayWrapper
                  className={styledOverlayWrapper}
                  text={t(
                    "project.fault_events.table.live.average_losses.tooltip.info",
                    translations,
                    aggregationPeriodLabel(aggregationPeriod).toLocaleLowerCase(),
                    project.showPartLoadSensitiveKpis,
                  )}
                />
              }
            >
              <span className={centerIcon}>
                <InformationLine size="s" color={colors.carbon800} />
              </span>
            </TooltipV2>
          </div>
        ) : null,
        hidden: performanceIndicator ? isHiddenColumn(AVERAGE_LOSSES_COLUMN) : true,
        render: (event: Event) => {
          if (performanceIndicator === null) return null;
          const eventId = getEventId(event);

          const sparklineStatistic = sparklineStatisticByFaultEventId.get(eventId);
          const charts = sparklineOptionsByFaultEventId.get(eventId);

          if (sparklineStatistic === undefined || charts === undefined)
            return (
              <Text className={noSparkline}>
                {t(
                  "project.fault_events.table.average_losses.no_sparkline",
                  aggregationPeriodLabel(aggregationPeriod).toLocaleLowerCase(),
                )}
              </Text>
            );

          const { mean } = sparklineStatistic;
          const isAround = isAroundZero(mean);

          return (
            <div
              className={cx(performanceIndicatorColumn, {
                [HAS_COLUMN_AVERAGE_LOSS]: true,
              })}
            >
              <div>
                {charts !== undefined && (
                  <EventTableSparkline
                    options={charts.options}
                    latestAnalysisDate={latestAnalysisDate}
                  />
                )}
              </div>
              {project.showPartLoadSensitiveKpis && (
                <Text>
                  {`${isAround ? "≈ " : ""}${formatNumber(mean, { precision: 1, sign: false })} ${performanceIndicator.unit || ""}`}
                </Text>
              )}
            </div>
          );
        },
        sortable: true,
        minWidth: "220px",
        maxWidth: "220px",
      },
      reparability: {
        label: (
          <div className={lineClamp}>
            {getLiveEventTableColumnsLabel("reparability", translations)}
          </div>
        ),
        hidden: isHiddenColumn("reparability"),
        render: (b: Event) =>
          b.reparability ? (
            <TooltipsV2
              placement="bottom"
              overlay={
                <OverlayWrapper
                  className={styledOverlayWrapper}
                  text={
                    <Text className={textActionTooltip}>
                      {t("reparability", b.reparability, translations)}
                    </Text>
                  }
                />
              }
            >
              <div>
                <Text className={textAction}>
                  {t("reparability", b.reparability, translations)}
                </Text>
              </div>
            </TooltipsV2>
          ) : null,
        minWidth: "150px",
        maxWidth: "150px",
        sortable: true,
      },
      action: {
        label: (
          <div className={lineClamp}>{getLiveEventTableColumnsLabel("action", translations)}</div>
        ),
        hidden: isHiddenColumn("action"),
        render: (event: Event) => {
          const identifiedAction = getEventIdentifiedAction(event);
          if (identifiedAction === null || identifiedAction === "") return null;

          return (
            <TooltipsV2
              placement="bottom"
              overlay={
                <OverlayWrapper
                  className={styledOverlayWrapper}
                  text={<Markdown className={actionTooltip} defaultValue={identifiedAction} />}
                />
              }
            >
              <div onClick={e => e.stopPropagation()}>
                <Markdown className={action} defaultValue={identifiedAction} />
              </div>
            </TooltipsV2>
          );
        },
        minWidth: "300px",
        maxWidth: "500px",
        sortable: false,
      },
      lastActivityAt: {
        label: (
          <div className={lineClamp}>
            {getLiveEventTableColumnsLabel("lastActivityAt", translations)}
          </div>
        ),
        hidden: isHiddenColumn("lastActivityAt"),
        render: (b: Event) => {
          const lastActivityAt = getEventLastActivityAt(b);
          if (!lastActivityAt) return null;
          if (lastActivityAt > new Date()) return null;
          const duration = new Date().getTime() - new Date(lastActivityAt).getTime();
          const fractional = millisecondsToFractional(duration);
          const date = formatDate(lastActivityAt, "date", project.timezone);
          return (
            <div className={datetime}>
              <span>{date}</span>
              <span>{t("datetime.fractional", { type: "date", ...fractional })}</span>
            </div>
          );
        },
        minWidth: "135px",
        maxWidth: "135px",
        sortable: true,
      },
    }),
    [
      performanceIndicator,
      project.timezone,
      project.showPartLoadSensitiveKpis,
      translations,
      isHiddenColumn,
      commentsByFaultEvents,
      allDiagnosedEvents,
      sparklineStatisticByFaultEventId,
      sparklineOptionsByFaultEventId,
      aggregationPeriod,
      commentsByManualFaultEvents,
      latestAnalysisDate,
      onUngroupEvent,
    ],
  );

  const gotoFaultEvent = React.useCallback(
    (event: Event, e: React.MouseEvent) => {
      const targetPath = gotoEventPage(event);

      if (e.metaKey || e.ctrlKey) {
        window.open(toBasenamePath(targetPath));
      } else {
        void navigate(targetPath, { state: { from: pathname } });
      }
    },
    [navigate, pathname],
  );

  return (
    <Table
      className={liveEventTable}
      stickyHeader
      loading={loading}
      // This has to be a stable object or we get infinite reload
      datas={stableSortedDatas}
      metadata={metadata}
      computeRowKey={computeRowKey}
      sort={sort}
      onSort={setSort}
      onRowClick={gotoFaultEvent}
      isRowInteractive={() => true}
      onRowsSelection={groupingSelectionEnabled ? onRowsSelection : undefined}
      selectAllRowsEnabled={!groupingSelectionEnabled}
      isRowSelectable={event =>
        isEventSelectableForGrouping({
          event,
          events: allEvents,
          selectedEvents: selectedEvents ?? [],
        })
      }
      selectedDatas={groupingSelectionEnabled ? selectedEvents : undefined}
    />
  );
};
export default LiveEventTable;

//#region CSS
const liveEventTable = css`
  .${SELECTABLE_ROW_CHECKBOX_CLASSNAME} {
    height: ${ROW_SELECTOR_SIZE_PX + 2}px;
    width: ${ROW_SELECTOR_SIZE_PX + 2}px;
  }
`;

const nameWrapper = css`
  display: flex;
  align-items: center;
  gap: ${spacings.xs};
  overflow-x: hidden;

  & > :first-child {
    flex: 1;
    min-width: 0;
  }
`;

const ungroupButton = css`
  flex-shrink: 0;
`;

const performanceIndicatorColumn = css`
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: 1fr 60px;
  align-items: center;

  & > :nth-child(1) {
    width: 100%;
    height: 100%;
  }

  & > :nth-child(2) {
    margin: auto;
  }
`;

const iconComment = css`
  display: flex;
  align-items: center;
  gap: ${spacings.xxs};
  padding: 10px 5px;
  color: ${colors.carbon900};

  &.empty {
    opacity: 50%;
  }
`;

const action = css`
  [data-lexical-editor] {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    text-overflow: ellipsis;
    overflow: hidden;
    -webkit-box-orient: vertical;
  }
`;
const actionTooltip = css`
  [data-lexical-editor] {
    color: ${color.hydrogen};
  }
`;
const styledOverlayWrapper = css`
  max-width: 350px;
`;

const datetime = css`
  height: 100%;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: repeat(2, 1fr);
  gap: ${spacings.xxs};

  span:nth-child(2) {
    font: ${fonts.body2};
    color: ${colors.carbon700};
  }
`;

const containerEndDate = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 8px;
`;

const flags = css`
  display: flex;
  gap: ${spacings.xs};
`;
const flag = css`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  border-radius: 50%;
  background-color: ${colors.titanium100};
`;
const lineClamp = css`
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;

  @supports (-webkit-line-clamp: 2) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: initial;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
`;
const labelInfo = css`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${spacings.xs};
  & > :last-child {
    flex-shrink: 0;
  }
`;
const textAction = css`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  text-overflow: ellipsis;
  overflow: hidden;
  -webkit-box-orient: vertical;
`;
const textActionTooltip = css`
  color: ${color.hydrogen};
`;
const tooltipTitleLastComment = css`
  margin-bottom: ${spacings.xs};
`;
const overlayWrapperLastComment = css`
  width: 286px;
`;
const noSparkline = css`
  color: ${colors.carbon500};
`;
const centerIcon = css`
  display: flex;
  align-items: center;
  justify-content: center;
`;
const eventPriority = css`
  display: flex;
  gap: ${spacings.xxs};
  align-items: center;
`;
//#endregion CSS
